import React from 'react'
import { Link } from 'react-router-dom'
import { useI18nContext } from '../hooks/I18nContext'

const Learning: React.FC = () => {
  const { t, tRaw } = useI18nContext()

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-blue-50 to-green-50">
      <div className="relative z-10">
        <main className="pt-20">
          {/* Hero Section */}
          <section className="py-20 px-4">
            <div className="max-w-6xl mx-auto text-center">
              <h1 className="text-5xl md:text-7xl font-bold text-gray-800 mb-6">
                {t('learning.hero.title')}
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
                {t('learning.hero.subtitle')}
              </p>
            </div>
          </section>

          {/* AI Teacher Lily */}
          <section className="py-16 px-4">
            <div className="max-w-6xl mx-auto">
              <div className="bg-white rounded-3xl p-12 shadow-lg">
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div>
                    <h2 className="text-4xl font-bold text-gray-800 mb-6">
                      {t('learning.lily.title')}
                    </h2>
                    <p className="text-lg text-gray-600 mb-8">
                      {t('learning.lily.description')}
                    </p>
                    <div className="space-y-4">
                      {(tRaw('learning.lilyFeatures') || []).map((feature: string, index: number) => (
                        <div key={index} className="flex items-center">
                          <div className="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center mr-4">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="w-48 h-48 bg-gradient-to-br from-pink-200 to-blue-200 rounded-full mx-auto mb-6 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-32 h-32 bg-white rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg">
                          <svg className="w-16 h-16 text-pink-500" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                          </svg>
                        </div>
                        <p className="text-gray-600 font-semibold">{t('learning.lilyName')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Learning Features */}
          <section className="py-16 px-4 bg-white">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
                {t('learning.features.title')}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {(tRaw('learning.learningFeatures') || []).map((feature: any, index: number) => (
                  <div key={index} className="bg-gray-50 rounded-2xl p-6 text-center">
                    <div className="text-4xl mb-4">{['📚', '💬', '📖', '🎮'][index]}</div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Educational Framework */}
          <section className="py-16 px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
                {t('learning.framework.title')}
              </h2>
              <div className="space-y-8">
                {(tRaw('learning.ageGroups') || []).map((level: any, index: number) => (
                  <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                    <div className="mb-4">
                      <h3 className={`text-2xl font-semibold ${['text-pink-600', 'text-blue-600', 'text-green-600'][index]} mb-2`}>
                        {level.age} - {level.title}
                      </h3>
                      <p className="text-gray-600">{level.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Download Materials */}
          <section className="py-16 px-4 bg-white">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
                {t('learning.materials.title')}
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {(tRaw('learning.downloads') || []).map((material: any, index: number) => (
                  <div key={index} className="bg-gray-50 rounded-2xl p-6 text-center">
                    <div className="text-4xl mb-4">{['📄', '📝', '🖼️'][index]}</div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {material.name}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {material.description}
                    </p>
                    <button className="bg-pink-500 text-white py-2 px-4 rounded-lg hover:bg-pink-600 transition-colors">
                      {t('learning.downloadButton')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-16 px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">
                {t('learning.cta.title')}
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                {t('learning.cta.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/buy"
                  className="bg-pink-500 text-white py-3 px-8 rounded-lg text-lg font-semibold hover:bg-pink-600 transition-colors"
                >
                  {t('learning.cta.button')}
                </Link>
                <Link
                  to="/start"
                  className="bg-white text-pink-500 py-3 px-8 rounded-lg text-lg font-semibold border-2 border-pink-500 hover:bg-pink-50 transition-colors"
                >
                  {t('learning.startButton')}
                </Link>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  )
}

export default Learning
